import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { 
  AlertTriangle, 
  ExternalLink, 
  Image, 
  Calendar,
  DollarSign,
  Percent,
  RefreshCw,
  Download,
  Filter,
  SortDesc
} from 'lucide-react';

// Module-level flags to prevent multiple initializations across component instances
// NEVER reset these during the session
let matchesPageInitialized = false;
let matchesPageInitInProgress = false;
let matchesPageApiCallsMade = false; // Track if API calls have been made (never reset)

const MatchesPage = ({ showNotification }) => {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('timestamp'); // timestamp, similarity
  const [filterBy, setFilterBy] = useState('all'); // all, high, medium, low
  const [searchTerm, setSearchTerm] = useState('');
  const initRef = useRef(false);

  const fetchMatches = useCallback(async (isInitialLoad = false) => {
    if (!matchesPageApiCallsMade) {
      console.log('[MatchesPage] fetchMatches skipped - API calls not allowed');
      // Still set loading to false to prevent infinite loading
      setLoading(false);
      return;
    }
    try {
      console.log('[MatchesPage] Calling fetchMatches...', isInitialLoad ? '(initial)' : '(polling)');
      // Only show loading state on initial load, not during polling
      if (isInitialLoad) {
        setLoading(true);
      }
      const response = await axios.get('https://outiloeuvresvolees.inouiagency.com/api/matches');
      console.log('[MatchesPage] API response:', response.data);
      
      // Handle different response formats
      const matches = response.data.matches || response.data || [];
      setMatches(Array.isArray(matches) ? matches : []);
      console.log('[MatchesPage] Set matches:', matches.length);
    } catch (error) {
      console.error('[MatchesPage] Error fetching matches:', error);
      setMatches([]); // Set empty array on error
      // Only show notification on initial load errors, not polling errors
      if (isInitialLoad) {
        showNotification('Error loading matches', 'error');
      }
    } finally {
      if (isInitialLoad) {
        setLoading(false);
        console.log('[MatchesPage] Loading set to false');
      }
    }
  }, [showNotification]);

  useEffect(() => {
    // CRITICAL: If API calls have already been made in this session, still fetch but don't mark again
    if (matchesPageApiCallsMade) {
      console.log('[MatchesPage] API calls already made in this session, but fetching fresh data...');
      initRef.current = true;
      // Still fetch matches to get fresh data, but don't reset the flag
      fetchMatches(true).catch(() => {
        // If fetch fails, ensure loading is set to false
        setLoading(false);
      });
      return;
    }
    
    // If already initialized, skip
    if (initRef.current) {
      console.log('[MatchesPage] Already initialized (initRef), skipping...');
      return;
    }
    
    // If another instance is initializing, skip
    if (matchesPageInitInProgress) {
      console.log('[MatchesPage] Initialization in progress, skipping...');
      return;
    }
    
    matchesPageInitialized = true;
    matchesPageInitInProgress = true;
    matchesPageApiCallsMade = true; // Mark as done - NEVER reset this
    initRef.current = true;
    
    console.log('[MatchesPage] Initializing (first time only in this session)...');
    fetchMatches(true).finally(() => {
      matchesPageInitInProgress = false;
    });
    
    // No cleanup - keep flags set
  }, [fetchMatches]);

  // Refresh matches when page becomes visible (replaces polling)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && matchesPageApiCallsMade) {
        console.log('[MatchesPage] Page became visible - refreshing matches');
        fetchMatches(false); // Don't show loading state for background refresh
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchMatches]);

  const getSimilarityLevel = (similarity) => {
    if (similarity >= 0.9) return 'high';
    if (similarity >= 0.8) return 'medium';
    return 'low';
  };

  const getSimilarityColor = (similarity) => {
    if (similarity >= 0.9) return 'text-red-600 bg-red-100';
    if (similarity >= 0.8) return 'text-orange-600 bg-orange-100';
    return 'text-yellow-600 bg-yellow-100';
  };

  const filteredAndSortedMatches = matches
    .filter(match => {
      // Filter by similarity level
      if (filterBy !== 'all') {
        const level = getSimilarityLevel(match.similarity || 0);
        if (level !== filterBy) return false;
      }
      
      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          match.title?.toLowerCase().includes(searchLower) ||
          match.stolen_image?.toLowerCase().includes(searchLower) ||
          match.price?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'similarity') {
        return (b.similarity ?? 0) - (a.similarity ?? 0);
      }
      return new Date(b.timestamp) - new Date(a.timestamp);
    });

  const exportMatches = async () => {
    try {
      showNotification('Exporting matches...', 'info');
      // This would typically call a backend endpoint to generate CSV
      const dataStr = JSON.stringify(filteredAndSortedMatches, null, 2);
      const dataBlob = new Blob([dataStr], {type: 'application/json'});
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `matches_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      showNotification('Matches exported successfully', 'success');
    } catch (error) {
      showNotification('Error exporting matches', 'error');
    }
  };

  const MatchCard = ({ match }) => {
    const similarity = match.similarity ?? 0;
    const similarityPercent = (similarity * 100).toFixed(1);
    const date = new Date(match.timestamp).toLocaleDateString();
    const time = new Date(match.timestamp).toLocaleTimeString();

    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow slide-in-up">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 line-clamp-2">
                {match.title || 'Unknown Listing'}
              </h3>
              <p className="text-sm text-gray-500">Found on {date} at {time}</p>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getSimilarityColor(similarity)}`}>
            {similarityPercent}% match
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          {/* Stolen Image */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Your Stolen Image</h4>
            <div className="bg-gray-100 rounded-lg p-4 text-center">
              {match.stolen_image ? (
                <img 
                  src={`https://outiloeuvresvolees.inouiagency.com/api/stolen-images/${(match.stolen_image || '').split('/').pop()}`}
                  alt="Stolen item"
                  className="w-full h-20 object-cover rounded mb-2"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <div style={{ display: match.stolen_image ? 'none' : 'block' }}>
                <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              </div>
              <p className="text-sm text-gray-600">
                {match.stolen_image ? match.stolen_image.split('/').pop() : 'Unknown'}
              </p>
            </div>
          </div>

          {/* Found Image */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Found on eBay</h4>
            <div className="bg-gray-100 rounded-lg p-4 text-center">
              {(match.found_image_url || match.found_image_local) ? (
                <img 
                  src={match.found_image_url || match.found_image_local} 
                  alt="Found item"
                  className="w-full h-20 object-cover rounded mb-2"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : null}
              <div style={{ display: (match.found_image_local || match.found_image_url) ? 'none' : 'block' }}>
                <Image className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              </div>
              <p className="text-sm text-gray-600">
                {match.found_image_local ? match.found_image_local.split('/').pop() : 
                 match.found_image_url ? 'eBay Image' : 'Unknown'}
              </p>
            </div>
          </div>
        </div>

        {/* Details */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center space-x-2">
            <Percent className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-gray-600">Similarity:</span>
            <span className="text-sm font-medium text-gray-900">{similarityPercent}%</span>
          </div>
          
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-purple-600" />
            <span className="text-sm text-gray-600">Detected:</span>
            <span className="text-sm font-medium text-gray-900">{date} {time}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          {match.source_url && (
            <a
              href={match.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              <span>View on eBay</span>
            </a>
          )}
          
          <button className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors text-sm">
            <Download className="h-4 w-4" />
            <span>Save Evidence</span>
          </button>
        </div>
      </div>
    );
  };

  const FilterControls = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        {/* Search */}
        <div className="flex-1 max-w-md">
          <input
            type="text"
            placeholder="Search matches..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={filterBy}
              onChange={(e) => setFilterBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Matches</option>
              <option value="high">High Similarity (≥90%)</option>
              <option value="medium">Medium Similarity (80-89%)</option>
              <option value="low">Low Similarity (&lt;80%)</option>
            </select>
          </div>

          <div className="flex items-center space-x-2">
            <SortDesc className="h-4 w-4 text-gray-500" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="timestamp">Sort by Date</option>
              <option value="similarity">Sort by Similarity</option>
            </select>
          </div>

          <button
            onClick={exportMatches}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
        </div>
      </div>
    </div>
  );

  const StatsOverview = () => {
    const highMatches = matches.filter(m => m.similarity >= 0.9).length;
    const mediumMatches = matches.filter(m => m.similarity >= 0.8 && m.similarity < 0.9).length;
    const lowMatches = matches.filter(m => m.similarity < 0.8).length;

    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{matches.length}</p>
          <p className="text-sm text-gray-600">Total Matches</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{highMatches}</p>
          <p className="text-sm text-gray-600">High Similarity</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-orange-600">{mediumMatches}</p>
          <p className="text-sm text-gray-600">Medium Similarity</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 text-center">
          <p className="text-2xl font-bold text-yellow-600">{lowMatches}</p>
          <p className="text-sm text-gray-600">Low Similarity</p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Found Matches</h1>
          <div className="flex items-center justify-center space-x-2">
            <RefreshCw className="h-5 w-5 animate-spin text-blue-600" />
            <span className="text-gray-600">Loading matches...</span>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
                <div className="w-20 h-6 bg-gray-200 rounded-full"></div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="h-20 bg-gray-200 rounded-lg"></div>
                <div className="h-20 bg-gray-200 rounded-lg"></div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Found Matches
        </h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Review potential matches found during your searches. Each match shows the similarity score and provides links to the original eBay listing.
        </p>
        <div className="flex justify-center mt-4">
          <button
            onClick={() => fetchMatches(true)}
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-800 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh Matches</span>
          </button>
        </div>
      </div>

      {matches.length > 0 ? (
        <>
          <StatsOverview />
          <FilterControls />
          
          {filteredAndSortedMatches.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredAndSortedMatches.map((match, index) => (
                <MatchCard key={index} match={match} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Filter className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No matches found</h3>
              <p className="text-gray-500">
                Try adjusting your filters or search terms.
              </p>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No matches yet</h3>
          <p className="text-gray-500 mb-6">
            Start a search to find potential matches with your stolen images.
          </p>
          <button
            onClick={() => window.location.href = '/search'}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Start Your First Search
          </button>
        </div>
      )}
    </div>
  );
};

export default MatchesPage;

